/**
 * PaymentSuccess - Public payment confirmation page (no auth required)
 * Square redirects here after checkout completion.
 * URL params: transactionId, orderId, referenceId (from Square)
 */
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle, Phone, Mail, Clock } from "lucide-react";

const PaymentSuccess = () => {
  const [firmName, setFirmName] = useState('');
  const [firmPhone, setFirmPhone] = useState('');
  useEffect(() => {
    fetch('/api/public/firm-info')
      .then(r => r.json())
      .then(d => { setFirmName(d.firm_name || ''); setFirmPhone(d.firm_phone || ''); })
      .catch(() => {});
  }, []);

  const [searchParams] = useSearchParams();
  const transactionId = searchParams.get("transactionId") || "";
  const orderId = searchParams.get("orderId") || "";
  const referenceId = searchParams.get("referenceId") || "";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        {/* Logo / Firm Name */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white tracking-wide">{firmName}</h1>
          <p className="text-slate-400 text-sm mt-1">Client Payment Portal</p>
        </div>

        {/* Success Card */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 backdrop-blur-sm">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-xl font-semibold text-white">Payment Received</h2>
            <p className="text-slate-400 mt-2">
              Thank you for your payment. Your transaction has been processed successfully.
            </p>
          </div>

          {/* Transaction Details */}
          {(transactionId || orderId || referenceId) && (
            <div className="bg-slate-900/50 rounded-xl p-4 mb-6 space-y-2">
              {referenceId && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Reference</span>
                  <span className="text-slate-200 font-mono">{referenceId}</span>
                </div>
              )}
              {transactionId && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Transaction ID</span>
                  <span className="text-slate-200 font-mono text-xs">{transactionId.slice(0, 16)}...</span>
                </div>
              )}
              {orderId && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Order ID</span>
                  <span className="text-slate-200 font-mono text-xs">{orderId.slice(0, 16)}...</span>
                </div>
              )}
            </div>
          )}

          {/* Next Steps */}
          <div className="space-y-3 mb-6">
            <h3 className="text-sm font-medium text-slate-300 uppercase tracking-wider">What Happens Next</h3>
            <div className="flex items-start gap-3">
              <Clock className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-slate-400">A confirmation receipt will be sent to your email address on file.</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-slate-400">Your invoice will be updated to reflect this payment. If you have portal access, you can view it there.</p>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-slate-700 pt-6">
            <h3 className="text-sm font-medium text-slate-300 uppercase tracking-wider mb-3">Contact Us</h3>
            <div className="space-y-2">
              {firmPhone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-slate-500" />
                  <a href={`tel:${firmPhone.replace(/[^0-9+]/g, '')}`} className="text-sm text-cyan-400 hover:text-cyan-300">{firmPhone}</a>
                </div>
              )}
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-slate-500" />
                <span className="text-sm text-slate-400">Or reply to the email from your attorney.</span>
              </div>
            </div>
          </div>

          {/* Portal Link */}
          <div className="mt-6 text-center">
            <a
              href="/portal/login"
              className="text-sm text-cyan-400 hover:text-cyan-300 underline"
            >
              Access Client Portal
            </a>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-slate-600 text-xs mt-6">
          &copy; {new Date().getFullYear()} {firmName || 'Our Firm'}. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default PaymentSuccess;
